'use client';

import { useState, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { CandidateProfile } from '@/lib/types/candidate';
import { CandidateList } from './CandidateList';
import { CandidateDetail } from './CandidateDetail';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Eye, EyeOff } from 'lucide-react';

export function PublicCandidatesView() {
  const [candidates, setCandidates] = useState<CandidateProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const searchParams = useSearchParams();
  const interviewId = searchParams.get('interview_id');
  // Initialize showAllCandidates from URL parameter
  const [showAllCandidates, setShowAllCandidates] = useState(
    searchParams.get('showAll') === 'true'
  );

  useEffect(() => {
    fetchCandidates();
  }, [interviewId, showAllCandidates]);

  useEffect(() => {
    // Easter egg: Ctrl/Cmd + Shift + A to toggle showing all candidates
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'A') {
        e.preventDefault();
        setShowAllCandidates(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const fetchCandidates = async () => {
    try {
      const params = new URLSearchParams();
      if (interviewId) params.append('interviewId', interviewId);
      if (showAllCandidates) params.append('showAll', 'true');
      
      const url = `/api/public/candidates${params.toString() ? '?' + params.toString() : ''}`;
      const response = await fetch(url);
      const data = await response.json();
      setCandidates(data.candidates || []);
    } catch (error) {
      console.error('Error fetching candidates:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCandidateDetails = (candidateId: string) => {
    setSelectedCandidateId(candidateId);
    setSheetOpen(true);
  };

  const handleCloseSheet = () => {
    setSheetOpen(false);
    setTimeout(() => setSelectedCandidateId(null), 300);
  };

  if (loading) {
    return (
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
        {[...Array(6)].map((_, i) => (
          <Skeleton key={i} className="h-[200px] w-full" />
        ))}
      </div>
    );
  }

  return (
    <>
      {/* Easter egg indicator */}
      {showAllCandidates && (
        <div className="mb-4 flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
          <div className="flex items-center gap-2">
            <Eye className="h-4 w-4 text-amber-600" />
            <span className="text-sm text-amber-800 font-medium">
              Showing all candidates (including scores below 75)
            </span>
          </div>
          <Badge variant="outline" className="text-xs bg-amber-100 text-amber-700 border-amber-300">
            Internal View
          </Badge>
        </div>
      )}
      
      <CandidateList
        filteredCandidates={candidates}
        openCandidateDetails={openCandidateDetails}
        openOfferModal={() => {}}
      />

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full max-w-6xl overflow-y-auto sm:max-w-[90vw] lg:max-w-[80vw]">
          <SheetHeader>
            <SheetTitle>Candidate Profile</SheetTitle>
          </SheetHeader>
          {selectedCandidateId && (
            <CandidateDetail
              candidateId={selectedCandidateId}
              isPublic={true}
              showQualifiedCandidate={true}
            />
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}